import { computed, defineComponent, ref, watch } from 'vue'
import { useResponsive } from '~/composables/useResponsive'
import Tabs from 'components/tabs'
import { Grid } from 'components/grid'
import BasicInput from 'components/form/basic-input'
import SelectDropDown from 'components/select-dropdown'
import { twColor } from '@/utils/style'
import FilterIcon from 'assets/img/svg/icons/filter.svg?raw'
import type { IResponsiveSearchFiltersProps } from './types'

const toValueString = (value: unknown) => (value ?? '') + ''

export const ResponsiveSearchFilters = defineComponent<IResponsiveSearchFiltersProps>(
	(props) => {
		const responsive = useResponsive()

		const query = ref(props.filters.queryString.value)

		watch(
			() => props.filters.queryString.value,
			(val) => {
				if (val !== query.value) query.value = val
			}
		)

		watch(
			() => query.value,
			(val) => {
				if (val !== props.filters.queryString.value) {
					props.filters.setQueryString(val)
				}
			}
		)

		const selectedCategory = computed(() => props.filters.selectedCategory.value)

		const desktopTabs = computed(() => {
			return (props.tabs || []).map((tab) => ({
				...tab,
				active: toValueString(tab.value) === selectedCategory.value
			}))
		})

		const categoryOptions = computed(() => {
			return (props.tabs || []).map((tab) => ({
				label: tab.title || tab.name || toValueString(tab.value),
				value: toValueString(tab.value),
				key: toValueString(tab.value)
			}))
		})

		const searchPlaceholder = computed(
			() => props.options?.searchPlaceholder || 'Search for...'
		)

		const handleTabClick = (index: number) => {
			const tab = props.tabs[index]
			if (!tab) return
			props.filters.selectCategory(toValueString(tab.value))
		}

		const handleCategorySelect = (value: string) => {
			props.filters.selectCategory(value)
		}

		const renderSearchInput = () => (
			<BasicInput
				value={query.value}
				type="text"
				placeholder={searchPlaceholder.value}
				iconLeft={{
					src: async () => import('assets/img/svg/icons/search.svg?raw'),
					size: 16,
					stroke: twColor('gray-500')
				}}
				onValueChange={(value: string) => {
					query.value = value || ''
				}}
			/>
		)

		return () => {
			if (responsive.isSmall.value) {
				const placeholder = categoryOptions.value[0]?.label || 'All Providers'
				return (
					<div class="w-full grid grid-auto-rows gap-2">
						<div class="w-full">{renderSearchInput()}</div>
						<div class="w-full">
							<SelectDropDown
								value={selectedCategory.value}
								onValueChange={handleCategorySelect}
								placeholder={placeholder}
								options={{
									items: categoryOptions.value,
									search: { enabled: false }
								}}
								icon={{
									src: FilterIcon,
									fill: twColor('gray-500'),
									stroke: twColor('gray-500'),
									size: 12
								}}
							/>
						</div>
					</div>
				)
			}

			return (
				<Grid
					columns="1fr max-content"
					justifyContent="space-between"
					alignItems="center"
					gap="1.5rem"
				>
					<Tabs tabs={desktopTabs.value} onTabClick={handleTabClick} />
					<div class="w-[250px]">{renderSearchInput()}</div>
				</Grid>
			)
		}
	},
	{ props: ['filters', 'tabs', 'options'] }
)

export * from './types'
export { useSearchFilters } from './hooks/useSearchFilters'

